"use client";

import React, { useState, useEffect, useRef, useCallback } from "react";
import Link from "next/link";
import {
  readConsent,
  saveConsent,
  OPEN_CONSENT_EVENT,
} from "@/lib/consent";

/**
 * Cookie- en privacyvoorkeuren als modaal venster.
 *
 * Bewust een modal en geen balkje onderin: de toestemming moet echt in beeld
 * komen, niet weg te scrollen zijn. Twee dingen zijn juridisch van belang en
 * mogen bij een herontwerp niet sneuvelen:
 *
 * 1. "Alleen noodzakelijk" en "Alles accepteren" hebben EXACT hetzelfde formaat
 *    en gewicht. Weigeren mag niet moeilijker of onopvallender zijn dan
 *    accepteren — daar let de Autoriteit Persoonsgegevens op. Maak van weigeren
 *    dus nooit een tekstlinkje of een lichter knopje.
 * 2. Er wordt niets geladen vóór een keuze. Wegklikken zonder te kiezen kan
 *    niet; Escape geldt als "alleen noodzakelijk" (de privacyvriendelijke kant),
 *    zodat toetsenbordgebruikers niet opgesloten zitten.
 *
 * Toegankelijkheid: role="dialog" + aria-modal, focus springt bij openen naar
 * het venster en keert daarna terug, Tab blijft binnen het venster, en de
 * achtergrond scrollt niet mee.
 */
export default function ConsentBanner() {
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);
  // Heropend via de footerlink? Dan is er al een keuze en mag Escape gewoon
  // annuleren zonder die keuze te overschrijven.
  const [isEditing, setIsEditing] = useState(false);

  const dialogRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!readConsent()) {
      const timer = setTimeout(() => setIsVisible(true), 800);
      return () => clearTimeout(timer);
    }
  }, []);

  useEffect(() => {
    const open = () => {
      const current = readConsent();
      setAnalytics(!!current?.analytics);
      setMarketing(!!current?.marketing);
      setShowDetails(true);
      setIsEditing(!!current);
      setIsVisible(true);
    };
    window.addEventListener(OPEN_CONSENT_EVENT, open);
    return () => window.removeEventListener(OPEN_CONSENT_EVENT, open);
  }, []);

  const close = useCallback(() => {
    setIsVisible(false);
    setShowDetails(false);
    setIsEditing(false);
  }, []);

  const finish = useCallback((analyticsChoice: boolean, marketingChoice: boolean) => {
    saveConsent(analyticsChoice, marketingChoice);
    close();
  }, [close]);

  // Achtergrond niet mee laten scrollen zolang het venster open staat.
  useEffect(() => {
    if (!isVisible) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = previous; };
  }, [isVisible]);

  // Focus vasthouden in het venster, en teruggeven bij sluiten.
  useEffect(() => {
    if (!isVisible) return;
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        // Bij het bewerken van bestaande voorkeuren: annuleren. Bij een eerste
        // bezoek: geldt als "alleen noodzakelijk", nooit als toestemming.
        if (isEditing) close();
        else finish(false, false);
        return;
      }
      if (e.key !== "Tab") return;

      const focusables = dialogRef.current?.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusables || focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      previouslyFocused.current?.focus?.();
    };
  }, [isVisible, isEditing, close, finish]);

  if (!isVisible) return null;

  return (
    // Vaste class voor de buitenste laag: de live-editor (VisualEditorBridge)
    // verbergt de banner in bewerk-modus via deze class — niet via de
    // inline-stijlen, die bij een herontwerp zomaar kunnen veranderen.
    <div
      className="mow-consent-overlay"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "rgba(38, 32, 28, 0.55)",
        backdropFilter: "blur(3px)",
        WebkitBackdropFilter: "blur(3px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
        animation: "mowConsentFade 0.25s ease forwards",
      }}
    >
      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes mowConsentFade { from { opacity: 0 } to { opacity: 1 } }
        @keyframes mowConsentIn {
          from { transform: translateY(16px) scale(0.98); opacity: 0 }
          to { transform: translateY(0) scale(1); opacity: 1 }
        }
        .mow-consent { animation: mowConsentIn 0.32s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        .mow-consent :focus-visible {
          outline: 3px solid #2f6f62;
          outline-offset: 2px;
          border-radius: 4px;
        }
        /* Over de volle breedte van het venster: "Alleen noodzakelijk" en "Alles
           accepteren" staan gelijkwaardig naast elkaar (elk de helft), met
           "Voorkeuren" eronder. De volgorde in de HTML is ook de volgorde van de
           Tab-toets. */
        .mow-consent-actions { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
        .mow-consent-actions button { width: 100%; }
        .mow-consent-actions .mow-tertiary { grid-column: 1 / -1; }
        @media (max-width: 560px) {
          /* Onder elkaar, maar NIET omgekeerd: weigeren staat boven accepteren,
             zodat accepteren ook op mobiel geen voorkeurspositie krijgt. */
          .mow-consent-actions { grid-template-columns: 1fr; }
        }
        @media (prefers-reduced-motion: reduce) {
          .mow-consent, .mow-consent * { animation: none !important; }
        }
      `}} />

      <div
        ref={dialogRef}
        className="mow-consent"
        role="dialog"
        aria-modal="true"
        aria-labelledby="mow-consent-title"
        aria-describedby="mow-consent-desc"
        tabIndex={-1}
        style={{
          background: "#ffffff",
          border: "1px solid #f3ede2",
          boxShadow: "0 24px 60px rgba(0,0,0,0.22)",
          borderRadius: "14px",
          padding: "28px",
          maxWidth: "560px",
          width: "100%",
          maxHeight: "90vh",
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: "18px",
          outline: "none",
        }}
      >
        <div>
          <h2
            id="mow-consent-title"
            style={{
              fontSize: "1.15rem",
              fontWeight: 700,
              margin: "0 0 10px",
              fontFamily: "var(--font-serif)",
              color: "var(--color-gray-800, #2b2b2b)",
            }}
          >
            Cookie- en privacyinstellingen
          </h2>
          <p
            id="mow-consent-desc"
            style={{ fontSize: "0.9rem", color: "var(--color-gray-600)", lineHeight: 1.65, margin: 0 }}
          >
            Wij gebruiken noodzakelijke cookies om de website te laten werken. Met uw toestemming
            gebruiken we ook analytische cookies (Google Analytics) om het gebruik te meten, en
            marketingcookies (Meta/Facebook, Klaviyo en Google Ads) om relevante advertenties te
            tonen. U kunt uw keuze later altijd aanpassen via <em>Cookie-instellingen</em> onderaan
            elke pagina.
          </p>

          <p style={{ fontSize: "0.85rem", margin: "12px 0 0", display: "flex", gap: "16px", flexWrap: "wrap" }}>
            <Link href="/privacybeleid" style={linkStyle}>Privacybeleid</Link>
            <Link href="/algemene-voorwaarden" style={linkStyle}>Algemene voorwaarden</Link>
          </p>
        </div>

        {showDetails && (
          <div style={{ display: "flex", flexDirection: "column", gap: "12px", borderTop: "1px solid #f3ede2", paddingTop: "16px" }}>
            <label style={rowStyle}>
              <input type="checkbox" checked disabled style={{ marginTop: "3px" }} />
              <span><strong>Noodzakelijk</strong> — vereist voor winkelmand, login en beveiliging. Altijd aan.</span>
            </label>
            <label style={{ ...rowStyle, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={analytics}
                onChange={(e) => setAnalytics(e.target.checked)}
                style={{ marginTop: "3px" }}
              />
              <span><strong>Analytisch</strong> — Google Analytics, om te meten hoe de website gebruikt wordt en die te verbeteren.</span>
            </label>
            <label style={{ ...rowStyle, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={marketing}
                onChange={(e) => setMarketing(e.target.checked)}
                style={{ marginTop: "3px" }}
              />
              <span><strong>Marketing</strong> — Meta/Facebook, Klaviyo en Google Ads, om relevante advertenties en e-mails te tonen en de campagneresultaten te meten.</span>
            </label>
          </div>
        )}

        <div className="mow-consent-actions">
          {/* Deze twee horen identiek van formaat en gewicht te blijven. */}
          <button className="mow-choice" onClick={() => finish(false, false)} style={btnRefuse}>
            Alleen noodzakelijk
          </button>
          <button className="mow-choice" onClick={() => finish(true, true)} style={btnAccept}>
            Alles accepteren
          </button>
          {!showDetails && (
            <button className="mow-tertiary" onClick={() => setShowDetails(true)} style={btnTertiary}>
              Voorkeuren
            </button>
          )}
          {showDetails && (
            <button className="mow-tertiary" onClick={() => finish(analytics, marketing)} style={btnTertiary}>
              Voorkeuren opslaan
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const linkStyle: React.CSSProperties = {
  color: "var(--color-secondary)",
  fontWeight: 600,
  textDecoration: "underline",
};

const rowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "flex-start",
  gap: "10px",
  fontSize: "0.85rem",
  color: "var(--color-gray-600)",
  lineHeight: 1.6,
};

// Accepteren en weigeren: identieke geometrie en typografie, alleen een andere
// kleurvlak. Wijzig dit paar altijd samen.
const btnChoiceBase: React.CSSProperties = {
  padding: "15px 22px",
  border: "1px solid transparent",
  borderRadius: "6px",
  fontSize: "1rem",
  fontWeight: 600,
  lineHeight: 1.2,
  cursor: "pointer",
  transition: "opacity 0.2s ease",
};

// Merkkleur (bordeaux) tegenover het neutrale donkerbruin van accepteren. Beide
// knoppen blijven even groot, even stevig gevuld en even goed leesbaar (wit op
// bordeaux ±12:1, wit op donkerbruin ±11:1). Een lichter grijs of omlijnd
// knopje laat weigeren optisch wegvallen, en dat is de ongelijkheid die niet mag.
const btnRefuse: React.CSSProperties = {
  ...btnChoiceBase,
  background: "var(--color-primary)",
  color: "#ffffff",
};

const btnAccept: React.CSSProperties = {
  ...btnChoiceBase,
  background: "#3f3a34",
  color: "#ffffff",
};

const btnTertiary: React.CSSProperties = {
  padding: "12px 18px",
  background: "transparent",
  border: "1px solid #dbc5b2",
  color: "var(--color-gray-600)",
  borderRadius: "6px",
  fontSize: "0.9rem",
  fontWeight: 500,
  cursor: "pointer",
  transition: "all 0.2s ease",
};
